/**
 * Smoothed design effects (brief 9.4, MET-03), implemented behind the
 * smoothDesignEffects stub in engine.ts.
 *
 * deff_d = Vhat_d / (phat_d (1 - phat_d) / n_d) is defined only for
 * 0 < phat_d < 1. One smoothed deff_c is produced per model cell:
 * - 'state' (default): the design effect of the cell's statewide direct
 *   estimate (the statewide domain computeDirectEstimates emits with
 *   areaId STATEWIDE_AREA_ID). When that domain carries the stepwise
 *   first-stage-FPC variance (DirectEstimate.fpcVariance — present when
 *   the design supplied PSU population counts), the design effect is
 *   computed from it: the statewide design effect with the first-stage
 *   finite population correction and certainty-PSU second-stage
 *   variance included (Tier 2 Item 2 package P2, Decision Log Addendum
 *   29). Otherwise it is computed from the with-replacement variance,
 *   exactly as shipped before P2.
 * - 'pooled': the median of deff_d over the cell's area domains with
 *   n_d >= 50 and at least 2 sampled PSUs (brief 9.4).
 * - 'fixed': the user-supplied constant.
 * - 'area-direct' (workstream F-1, Decision Log Addendum 56;
 *   construction as registered in validation/simulation/results/
 *   FRONTIER_F1_PSI_RESULTS.md): the cell value returned is the
 *   'state' value itself — the shipped P2 basis, stepwise-FPC when
 *   the design supplied frame counts, with the brief 9.4 clamp and
 *   the shipped fallback chain applied exactly as shipped. The
 *   F-1 construction is a per-domain blend formed in the transform
 *   stage (transform.ts): each domain's design effect moves from
 *   this cell value toward the domain's own stepwise design effect
 *   with weight df_d / (df_d + K). Nothing in this stage differs
 *   from the 'state' computation under this method.
 * - 'within-area' (candidate W, Decision Log Addendum 50 Item 1;
 *   construction as registered in validation/simulation/results/
 *   TIER2_W_BASIS_RESULTS.md, iteration 2): the pooled within-area
 *   design effect deff_w — the ratio of the sums of the cell's own
 *   domain variances to the sums of their SRS benchmarks over the
 *   cell's domains with interior phat_d. The pooled variances are
 *   the domains' stepwise first-stage-FPC variances (direct.ts)
 *   when the design supplied frame counts — the design-true
 *   within-area variances, P2's certainty-PSU handling included —
 *   and their with-replacement variances otherwise. Each is formed
 *   from the area's own strata and PSUs, so clustering and
 *   within-area weighting enter and between-area weight variation
 *   cannot. The transform stage multiplies deff_w by each area's
 *   own first-stage factor FPC_d (see transform.ts). The [min, max]
 *   clamp does NOT apply under this method (Addendum 50: the clamp
 *   must not apply to the FPC-adjusted area value); the only bound
 *   on deff_w is the registered stability floor
 *   WITHIN_AREA_DEFF_FLOOR, and `clamped` records whether that
 *   floor bound. An empty or degenerate pool falls back to the
 *   'state' value (and from there to the chain below).
 * The result is clamped to [min, max] (defaults 1.0 and 5.0) under
 * every method except 'within-area'; `clamped` records whether the
 * bound changed the value.
 *
 * Fallbacks (each sets fallbackUsed, matching results.ts):
 * - If the statewide proportion for a cell is 0 or 1, the brief directs
 *   using the indicator's overall design effect: the cell falls back to
 *   the overall cell's (already computed) deff_c. If the overall cell's
 *   statewide proportion is itself 0 or 1, no design effect in the
 *   indicator is defined and deff_c = 1.0 (the lower clamp bound) is used.
 * - 'pooled' with no qualifying area domains falls back to the 'state'
 *   value (and from there to the chain above).
 * A statewide variance of exactly 0 with an interior proportion yields
 * deff = 0 by the formula, which the clamp then lifts to min; that is a
 * clamp, not a fallback.
 */
import { STATEWIDE_AREA_ID } from './direct.js';
import { clamp } from './numeric.js';
/** deff of one domain from a given variance, or undefined when the formula does not apply. */
function deffFromVariance(est, variance) {
    if (!(est.estimate > 0 && est.estimate < 1))
        return undefined;
    if (!(est.n > 0) || Number.isNaN(variance))
        return undefined;
    return variance / ((est.estimate * (1 - est.estimate)) / est.n);
}
/** deff of one domain from its with-replacement variance. */
function domainDeff(est) {
    return deffFromVariance(est, est.variance);
}
/**
 * Registered stability floor for the pooled within-area design
 * effect (candidate W; Decision Log Addendum 50 Item 1 and the
 * study registration in validation/simulation/results/
 * TIER2_W_BASIS_RESULTS.md): deff_w never reads below 0.05. There
 * is no ceiling under the 'within-area' method.
 */
export const WITHIN_AREA_DEFF_FLOOR = 0.05;
function median(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;
}
export function smoothDesignEffectsImpl(estimates, options) {
    const groups = new Map();
    const order = [];
    for (const est of estimates) {
        let group = groups.get(est.domain.cellId);
        if (!group) {
            group = {
                cellId: est.domain.cellId,
                indicatorId: est.domain.indicatorId,
                isOverall: est.domain.subgroupVariable === undefined,
                areas: [],
            };
            groups.set(est.domain.cellId, group);
            order.push(est.domain.cellId);
        }
        if (est.domain.areaId === STATEWIDE_AREA_ID)
            group.statewide = est;
        else
            group.areas.push(est);
    }
    // Overall cells are resolved first: subgroup fallbacks read them.
    const resolved = new Map();
    const resolveCell = (group) => {
        const stateDeff = group.statewide ? domainDeff(group.statewide) : undefined;
        // Tier 2 Item 2 package P2 (Decision Log Addendum 29): when the
        // statewide domain carries the stepwise first-stage-FPC variance
        // (the design supplied PSU population counts), the 'state' basis is
        // the design effect of THAT variance. Absent the corrected
        // variance the basis is exactly the shipped with-replacement one.
        // The 'pooled' method and its 'state' fallback keep the
        // with-replacement value: the pooled construction was validated as
        // shipped (study stepA) and P2 changes only the 'state' basis.
        const stateFpcDeff = group.statewide?.fpcVariance !== undefined
            ? deffFromVariance(group.statewide, group.statewide.fpcVariance)
            : undefined;
        let raw;
        let fallbackUsed = false;
        let floorBound = false;
        const studyOverride = options.studyOverrideByCellId?.get(group.cellId);
        if (studyOverride !== undefined && Number.isFinite(studyOverride)) {
            // Study-only psi basis (see DesignEffectOptions): the supplied
            // value replaces the method computation for this cell.
            raw = studyOverride;
        }
        else if (options.method === 'fixed') {
            if (options.fixedValue === undefined) {
                throw new Error("smoothDesignEffects: method 'fixed' requires options.fixedValue");
            }
            raw = options.fixedValue;
        }
        else if (options.method === 'pooled') {
            const candidates = [];
            for (const est of group.areas) {
                if (est.n >= 50 && est.psuCount >= 2) {
                    const d = domainDeff(est);
                    if (d !== undefined)
                        candidates.push(d);
                }
            }
            if (candidates.length > 0) {
                raw = median(candidates);
            }
            else {
                fallbackUsed = true;
                raw = stateDeff;
            }
        }
        else if (options.method === 'within-area') {
            // Candidate W (Decision Log Addendum 50 Item 1): the pooled
            // within-area design effect, a ratio of sums over the cell's
            // own domains (see the header). No cross-area total is formed,
            // so between-area weight variation enters nowhere. The pooled
            // variance is the domain's stepwise FPC variance when the
            // counts channel is active for the cell (the statewide domain
            // carries fpcVariance exactly then), else its with-replacement
            // variance.
            const useFpc = group.statewide?.fpcVariance !== undefined;
            let num = 0;
            let den = 0;
            for (const est of group.areas) {
                if (!(est.n > 0) || !(est.estimate > 0 && est.estimate < 1))
                    continue;
                const v = useFpc ? est.fpcVariance : est.variance;
                if (v === undefined || !Number.isFinite(v) || v < 0)
                    continue;
                num += v;
                den += (est.estimate * (1 - est.estimate)) / est.n;
            }
            const pooled = den > 0 ? num / den : Number.NaN;
            if (Number.isFinite(pooled) && pooled > 0) {
                raw = Math.max(pooled, WITHIN_AREA_DEFF_FLOOR);
                floorBound = raw !== pooled;
            }
            else {
                fallbackUsed = true;
                raw = stateFpcDeff ?? stateDeff;
            }
        }
        else {
            // 'state'
            raw = stateFpcDeff ?? stateDeff;
        }
        if (raw === undefined) {
            // Brief 9.4 fallback: the indicator's overall design effect.
            fallbackUsed = true;
            if (!group.isOverall) {
                const overall = [...groups.values()].find((g) => g.indicatorId === group.indicatorId && g.isOverall);
                if (overall) {
                    const overallEffect = resolved.get(overall.cellId) ?? resolveCell(overall);
                    raw = overallEffect.designEffect;
                }
            }
            if (raw === undefined)
                raw = 1.0;
        }
        // The brief 9.4 [min, max] clamp does not apply under
        // 'within-area' (Addendum 50 Item 1: the clamp must not apply to
        // the FPC-adjusted area value; the pooled value itself carries
        // only the registered stability floor, applied in the branch
        // above, including on its 'state' fallback path).
        const designEffect = options.method === 'within-area' ? raw : clamp(raw, options.min, options.max);
        const result = {
            cellId: group.cellId,
            designEffect,
            method: options.method,
            clamped: options.method === 'within-area' ? floorBound : designEffect !== raw,
            fallbackUsed,
        };
        resolved.set(group.cellId, result);
        return result;
    };
    // Resolve overall cells first (subgroup fallbacks read them), but return
    // one entry per cell in first-appearance order.
    for (const id of order) {
        const group = groups.get(id);
        if (group?.isOverall)
            resolveCell(group);
    }
    return order.map((id) => {
        const group = groups.get(id);
        if (!group)
            throw new Error(`smoothDesignEffects: lost cell ${id}`);
        return resolved.get(id) ?? resolveCell(group);
    });
}
//# sourceMappingURL=deff.js.map